import { DEBUG_MODE, isiOSApp, isWebKit } from "./client";

const MAX_TIME_HIDDEN_BEFORE_RELOAD_S = 10 * 60; // 10 minutes

export enum VisibilityState {
    Visible = "visible",
    Hidden = "hidden",
}

/// This is a simple class that keeps track of the visibility state of the page.
///
/// There seems to be a problem with backgrounding the app on iOS, so we need to
/// keep track of the visibility state and reload the page if it's been hidden for
/// too long. Thanks Sue!
export class VisibilityStates {
    private static lastKnownState: VisibilityState = VisibilityState.Visible;
    private static updatedTimestamp: number = Date.now();

    public static initialise() {
        // iOS has it's own handlers for this
        if (isiOSApp()) {
            return;
        }

        // Add event listener for visibility change
        document.addEventListener("visibilitychange", () => {
            this.updateVisibilityState(document.visibilityState as VisibilityState);
        });

        // Add extra listeners to improve handling
        window.addEventListener("focus", () => {
            this.updateVisibilityState(VisibilityState.Visible);
        });

        window.addEventListener("blur", () => {
            this.updateVisibilityState(VisibilityState.Hidden);
        });

        // Try and better detect unload events
        window.addEventListener("beforeunload", () => {
            this.updateVisibilityState(VisibilityState.Hidden);
        });

        window.addEventListener("unload", () => {
            this.updateVisibilityState(VisibilityState.Hidden);
        });
    }

    /// Update the visibility state
    public static updateVisibilityState(nextState: VisibilityState) {
        // Detect when state changes
        if (nextState !== this.lastKnownState) {
            this.lastKnownState = nextState;
            if (DEBUG_MODE) {
                console.log(`updateVisibilityState ${nextState}`);
            }
            if (nextState === VisibilityState.Visible) {
                this.onBecameVisible();
            } else {
                this.onWentInactive();
            }
        }
        this.updatedTimestamp = Date.now();
    }

    /// Called when the page becomes visible
    public static onBecameVisible() {
        const currentTime = Date.now();
        const timeHidden = currentTime - this.updatedTimestamp;

        if (
            // Check how long the page was hidden
            timeHidden > MAX_TIME_HIDDEN_BEFORE_RELOAD_S * 1000 &&
            // Ensure the state hasn't changed just before reloading
            this.lastKnownState === VisibilityState.Visible &&
            // Reloading only seems to be required on iOS and webkit
            (isiOSApp() || isWebKit())
        ) {
            if (DEBUG_MODE) {
                console.log(`onBecameVisible Reloading page after ${timeHidden / 1000}s`);
            }
            window.location.reload();
        } else {
            if (DEBUG_MODE) {
                console.log(
                    `onBecameVisible Page was hidden for ${
                        timeHidden / 1000
                    }s, reload not necessary`
                );
            }
        }
    }

    public static onWentInactive() {}
}
